import React from 'react'

import { Box, Button, SelectOption } from '@factorialco/deprecated-design-system'
import { useToggle } from 'usehooks-ts'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type Company from 'modules/core/store/models/Company'

import { validateSubdomain } from 'modules/company_page/lib/utils/validateSubdomain'
import CompanyGeneralForm from 'modules/settings/lib/forms/General'

interface Props {
  company: Company
  domains: string[]
}

export const CompanyPageUrlForm = observer(({ company, domains }: Props) => {
  const form = React.useRef(new CompanyGeneralForm(company.toJS())).current
  const [invalidSlug, setInvalidSlug] = React.useState(false)
  const [saving, savingToggle] = useToggle(false)
  const urlSlugField = form.get('slug')
  const urlDomainField = form.get('country_factorial_domain')

  const domainOptions: SelectOption[] = domains.map((domain) => {
    return {
      label: domain,
      value: domain,
    }
  })

  useEffect(() => {
    const currentDomain = domainOptions.find((option) => option.value === urlDomainField.value)
    if (!currentDomain) {
      urlDomainField.set(domainOptions[0]?.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSlugChange = (newSlug: string) => {
    urlSlugField.set(newSlug)
    validateSubdomain(urlSlugField)
    setInvalidSlug(urlSlugField.value === '' || !!urlSlugField.errors?.length)
  }

  const handleDomainChange = (newDomain: string) => {
    urlDomainField.set(newDomain)
  }

  const submitForm = async () => {
    savingToggle()
    const data = form.data()
    if (urlSlugField.errors) {
      savingToggle()
      return
    }

    form.setValues(data)
    await asyncAction(() => company.updateSlugRpc(form, urlSlugField.value, urlDomainField.value), {
      errorFlash: false,
    })
    savingToggle()
  }

  const subdomainInformation = urlSlugField.errors ? urlSlugField.errors[0] : undefined

  return (
    <Box padding="s8" gap="s12" alignItems="flexEnd">
      <Box width="full" flexDirection="row" alignItems="flexStart" gap="s8">
        <TextField
          readonly={saving}
          error={subdomainInformation}
          placeholder={i18n.t('settings.edit_company.company_subdomain_placeholder')}
          value={urlSlugField.value}
          onChange={(newSlug) => handleSlugChange(newSlug)}
          label={i18n.t(
            'company_settings.general.company_page.options.company_page.subdomain_label'
          )}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t(
                'company_settings.general.company_page.options.company_page.subdomain_tip'
              )}
              icon="info"
            />
          }
        />
        <SelectField
          options={domainOptions}
          value={urlDomainField.value}
          onChange={(newDomain) => handleDomainChange(newDomain)}
          readonly={saving}
          label={i18n.t('company_settings.general.company_page.options.company_page.domain_label')}
        />
      </Box>

      {form.isDirty && (
        <Button disabled={saving || invalidSlug} onClick={submitForm}>
          {i18n.t('common.submit')}
        </Button>
      )}
    </Box>
  )
})
